import React from 'react';

// Tab components
import OverviewTab from '../admin/OverviewTab';
import UserManagementTab from '../admin/UserManagementTab';
import UserApprovalTab from '../admin/UserApprovalTab';
import SettingsTab from '../admin/SettingsTab';
import SubscriptionPlansTab from '../admin/SubscriptionPlansTab';
import SubscriptionManagementTab from '../admin/SubscriptionManagementTab';
import OrderManagementTab from '../admin/OrderManagementTab';
import ScraperSettingsTab from '../admin/ScraperSettingsTab';
import ReportsTab from '../admin/ReportsTab';
import PartnerWalletAdminTab from '../admin/PartnerWalletAdminTab';
import PartnerWalletLedgerTab from '../admin/PartnerWalletLedgerTab';

/**
 * TabRenderer - Handles rendering of admin tab content
 * Responsibilities: Tab content rendering and prop passing
 */
const TabRenderer = ({
  activeTab,
  adminData,
  subscriptionPlans,
  adminSettings,
  userManagement,
  appSettings
}) => {
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <OverviewTab
            stats={adminData.stats}
            loading={adminData.loading}
            error={adminData.error}
            appSettings={appSettings}
          />
        );

      case 'users':
        return (
          <UserManagementTab
            users={adminData.users}
            setUsers={adminData.setUsers}
            loading={adminData.loading}
            error={adminData.error}
            showAddUser={userManagement.showAddUser}
            setShowAddUser={userManagement.setShowAddUser}
            editingUser={userManagement.editingUser}
            setEditingUser={userManagement.setEditingUser}
            formData={userManagement.formData}
            setFormData={userManagement.setFormData}
            handleInputChange={(e) => {
              const { name, value } = e.target;
              userManagement.setFormData(prev => ({ ...prev, [name]: value }));
            }}
            handleAddUser={async (userData) => {
              const result = await userManagement.createUser(userData);
              if (result.success) {
                await adminData.fetchUsers(); // Refresh users list
                // Success feedback handled by modal
              } else {
                alert(result.message); // Show error if creation fails
              }
              return result; // Return result for modal handling
            }}
            handleUpdateUser={async (userId, userData) => {
              const result = await userManagement.updateUser(userId, userData);
              if (result.success) {
                await adminData.fetchUsers(); // Refresh users list
                // Success feedback handled by modal
              } else {
                alert(result.message); // Show error if update fails
              }
              return result; // Return result for modal handling
            }}
            handleDeleteUser={async (userId) => {
              const result = await userManagement.deleteUser(userId);
              if (result.success) {
                await adminData.fetchUsers(); // Refresh users list
              } else {
                alert(result.message);
              }
              return result;
            }}
            handleDeactivateUser={async (userId, reason) => {
              const result = await userManagement.deactivateUser(userId, reason);
              if (result.success) {
                await adminData.fetchUsers(); // Refresh users list
                alert('User deactivated successfully');
              } else {
                alert(result.message); // Show error if deactivation fails
              }
              return result;
            }}
            handleActivateUser={async (userId) => {
              const result = await userManagement.activateUser(userId);
              if (result.success) {
                await adminData.fetchUsers(); // Refresh users list
                alert('User activated successfully');
              } else {
                alert(result.message); // Show error if activation fails
              }
              return result;
            }}
            handleAssignRole={async (userId, role, adminRole, reason) => {
              const result = await userManagement.assignUserRole(userId, role, adminRole, reason);
              if (result.success) {
                await adminData.fetchUsers(); // Refresh users list
                alert('User role assigned successfully');
              } else {
                alert(result.message); // Show error if role assignment fails
              }
              return result;
            }}
            isDeleting={userManagement.isDeleting}
            handleLoginAsUser={async (userId) => {
              try {
                const adminToken = localStorage.getItem('admin_token');
                
                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}/login-as`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${adminToken}`,
                    'Content-Type': 'application/json'
                  }
                });
                
                if (!response.ok) {
                  throw new Error('Failed to login as user');
                }
                
                const data = await response.json();
                
                // Open new tab with user token passed as query parameter
                const userDashboardUrl = `/dashboard?loginAsToken=${encodeURIComponent(data.access_token)}&userData=${encodeURIComponent(JSON.stringify(data.user))}`;
                window.open(userDashboardUrl, '_blank');
                
              } catch (error) {
                console.error('Error logging in as user:', error);
                alert('Failed to login as user. Please try again.');
              }
            }}
            fetchUsers={adminData.fetchUsers}
            appSettings={appSettings}
          />
        );

      case 'user-approvals':
        return (
          <UserApprovalTab />
        );

      case 'settings':
        return (
          <SettingsTab
            settings={adminSettings.settings}
            settingsLoading={adminSettings.settingsLoading}
            updateSettings={adminSettings.updateSettings}
          />
        );
        
      case 'subscriptions':
        return (
          <SubscriptionPlansTab
            subscriptionPlans={subscriptionPlans.subscriptionPlans}
            users={adminData.users}
            loading={adminData.loading}
            error={adminData.error}
            showAddPlan={subscriptionPlans.showAddPlan}
            setShowAddPlan={subscriptionPlans.setShowAddPlan}
            editingPlan={subscriptionPlans.editingPlan}
            setEditingPlan={subscriptionPlans.setEditingPlan}
            showAssignPlan={subscriptionPlans.showAssignPlan}
            setShowAssignPlan={subscriptionPlans.setShowAssignPlan}
            selectedUserForPlan={subscriptionPlans.selectedUserForPlan}
            setSelectedUserForPlan={subscriptionPlans.setSelectedUserForPlan}
            planFormData={subscriptionPlans.planFormData}
            setPlanFormData={subscriptionPlans.setPlanFormData}
            handleCreatePlan={subscriptionPlans.createSubscriptionPlan}
            handleUpdatePlan={subscriptionPlans.updateSubscriptionPlan}
            handleDeletePlan={subscriptionPlans.deleteSubscriptionPlan}
            handleAssignPlan={subscriptionPlans.assignPlanToUser}
          />
        );

      case 'subscription-management':
        return (
          <SubscriptionManagementTab
            settings={appSettings}
          />
        );

      case 'order-management':
        return (
          <OrderManagementTab />
        );

      case 'scraper-settings':
        return (
          <ScraperSettingsTab />
        );

      case 'reports':
        return (
          <ReportsTab />
        );
      
      case 'partner-wallet-admin':
        return (
          <PartnerWalletAdminTab />
        );
        
      case 'partner-wallet-ledger':
        return (
          <PartnerWalletLedgerTab />
        );
        
      default:
        return null;
    }
  };

  return renderTabContent();
};

export default TabRenderer;